import React, { useState, useMemo, useEffect } from "react";
import Button from "../../../components/FormComponents/Button";
import SingleSelectBox from "../../../components/FormComponents/SingleSelectBox";
import ErrorMessage from "../../../components/FormComponents/ErrorMessage";
import BuilderRightSideBar from "../../../Layouts/BuilderRightSideBar";
import { getOrganisationsApi } from "../../../services/apis/organisationApis";
import useQuery from "../../../hooks/useQuery";
import Textarea from "../../../components/FormComponents/Textarea";

const ORG_LIST = ["organisations-list"];

const CreateReleaseSidebar = ({ isOpen, onClose, onSubmit }) => {
  const { data: orgsResponse, isLoading: isLoadingOrgs } = useQuery(ORG_LIST, getOrganisationsApi);
  const organisations = useMemo(() => orgsResponse?.data || [], [orgsResponse]);
  const [formData, setFormData] = useState({
    org_id: "",
    org_name: "",
    app_id: "",
    app_name: "",
    type: "application",
    reason: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const orgOptions = useMemo(() => {
    return organisations.map((org) => ({ label: org.name, value: org.id }));
  }, [organisations]);

  const appOptions = useMemo(() => {
    if (!formData.org_id) return [];
    const selectedOrg = organisations.find((org) => org.id === formData.org_id);
    return selectedOrg?.apps?.map((app) => ({ label: app.name, value: app.id })) || [];
  }, [organisations, formData.org_id]);

  const handleOrgChange = (option) => {
    setFormData((prev) => ({
      ...prev,
      org_id: option.value,
      org_name: option.label,
      app_id: "", // Reset app
      app_name: ""
    }));
  };

  const handleAppChange = (option) => {
    setFormData((prev) => ({
      ...prev,
      app_id: option.value,
      app_name: option.label
    }));
  };

  const handleReasonChange = (reason) => {
    setFormData((prev) => ({
      ...prev,
      reason: reason
    }));
  };

  const handleSubmit = async () => {
    if (!formData.app_id || !formData.org_id || !formData.reason.trim()) {
      setError("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const payload = {
        type: "application",
        app_id: formData.app_id,
        app_name: formData.app_name,
        org_id: formData.org_id,
        org_name: formData.org_name,
        reason: formData.reason
      };

      await onSubmit(payload);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Failed to create release.");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setFormData({
        org_id: "",
        org_name: "",
        app_id: "",
        app_name: "",
        version: "",
        reason: "",
        type: "application"
      });
      setError(null);
    }
  }, [isOpen]);

  return (
    <BuilderRightSideBar isOpen={isOpen} onClose={onClose} heading="New Release" size="small">
      <div className="d_flex flex_column h_100">
        <div className="flex_grow overflow_auto p_3 d_flex flex_column gap_4">
          <div className="d_flex flex_column gap_3">
            <SingleSelectBox
              label="Organization*"
              placeholder={isLoadingOrgs ? "Loading..." : "Select Organization"}
              options={orgOptions}
              labelKey="label"
              valueKey="value"
              selectedData={formData.org_id}
              onChange={handleOrgChange}
              containerClassName="w_100"
              isSearch={true}
            />

            <SingleSelectBox
              label="Application*"
              placeholder="Select Application"
              options={appOptions}
              labelKey="label"
              valueKey="value"
              selectedData={formData.app_id}
              onChange={handleAppChange}
              containerClassName="w_100"
              disabled={!formData.org_id}
              containerStyle={!formData.org_id ? { opacity: 0.5, pointerEvents: "none" } : {}}
              isSearch={true}
            />

            <Textarea
              label="Reason for Release*"
              value={formData.reason}
              onChange={(e) => handleReasonChange(e.target.value)}
              placeholder="Reason For Initiating Release"
            />
          </div>

          <div className="border_bottom"></div>

          {error && <ErrorMessage>{error}</ErrorMessage>}
        </div>

        {/* --- Footer --- */}
        <div className="p_3 border_top d_flex justify_end gap_2 bg_gray">
          <Button variant="secondary" onClick={() => onClose(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            disabled={!formData.app_id || isSubmitting}
          >
            Create
          </Button>
        </div>
      </div>
    </BuilderRightSideBar>
  );
};

export default CreateReleaseSidebar;
